import React, { useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import { PresentationsSelectionPage } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/pages/Selection/PresentationsSelectionPage';
import { PresentationsTypePage } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/pages/Type/PresentationsTypePage';
import { PresentationsTabsPath } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/types';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { PresentationsBrandsPage } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/pages/Brands/PresentationsBrandsPage';
import { PresentationsConfirmationPage } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/pages/Confirmation/PresentationsConfirmationPage';
import { validationSteps } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/constants';
import BuilderFooter from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/components/BuilderFooter';
import { usePresentationFormContext } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/PresentationFormContext';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { getCachedPresentationFormState } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/PresentationFormCache';
import { useLocation } from 'src/utils/Hooks';

const VALID_STEP_PATHS = new Set<string>(Object.values(PresentationsTabsPath));

export const PresentationsBuilder = () => {
  const { beaconNavigator } = useBeaconNavigator();
  const { isValidStep } = usePresentationFormContext();
  const { pathname } = useLocation();

  useEffect(() => {
    // If the pathname isn't a recognized step (e.g. `/account/presentations`
    // without a sub-route), always redirect to /type so the Switch has a match.
    if (!VALID_STEP_PATHS.has(pathname)) {
      beaconNavigator.setPathname(PresentationsTabsPath.type).go();
      return;
    }

    // On a transient remount (HomeAuthLayout loading cycle), the cache has the
    // user's form data but stepValidity is still the initial all-false useState.
    // Skip enforcement — forward navigation is guarded by BuilderFooter and
    // PresentationsNavigationTabs.
    const cached = getCachedPresentationFormState();
    if (cached.type) {
      return;
    }

    // Cold first visit: enforce step validation
    const validationStepsList = Object.keys(validationSteps);
    for (let i = 0; i < validationStepsList.length; i++) {
      const isValid = isValidStep(validationStepsList[i] as PresentationsTabsPath);
      if (!isValid) {
        beaconNavigator.setPathname(validationStepsList[i]).go();
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <BasePageContainer>
      <Switch>
        <Route exact path={PresentationsTabsPath.type} component={PresentationsTypePage} />
        <Route exact path={PresentationsTabsPath.selections} component={PresentationsSelectionPage} />
        <Route exact path={PresentationsTabsPath.brands} component={PresentationsBrandsPage} />
        <Route exact path={PresentationsTabsPath.confirmations} component={PresentationsConfirmationPage} />
      </Switch>
      <BuilderFooter />
    </BasePageContainer>
  );
};
